/**
 * Theme color injection: generate --zoom-color-* CSS variables
 * from Base Color (H,S,L) following the rules in brand-color.md.
 */

const BRAND_STYLE_ID = 'brand-color-sheet'

/** Luminance thresholds for color adjustment strategies */
const LUMINANCE = {
  /** L >= 80% is treated as an ultra-light theme color */
  LIGHT_THRESHOLD: 80,
  /** L <= 20% is treated as an ultra-dark theme color */
  DARK_THRESHOLD: 20
}

/** Opacity values for subtle interactive states */
const OPACITY = {
  SUBTLE_HOVER: 0.12,
  SUBTLE_PRESS: 0.42
}

/** Lightness offsets for generating color variants (from brand-color.md) */
const L_OFFSET = {
  // Format: { normal, light, dark } corresponds to luminance ranges
  TEXT_PRIMARY: { normal: 0, light: -40, dark: 40 },
  BORDER_PRIMARY: { normal: 15, light: -30, dark: 30 },
  STATE_HOVER: { normal: -10, light: -15, dark: 15 },
  STATE_PRESS: { normal: -25, light: -30, dark: 30 },
  FILL_SUBTLER: { normal: 48, light: 0, dark: 70 },
  STATE_SUBTLE: { normal: 0, light: -20, dark: 20 },
  BORDER_SUBTLE: { normal: 35, light: -15, dark: 50 },
  TOGGLE_BG: { normal: 45, light: -5, dark: 60 },
  TEXT_STRONG: { normal: -15, light: -50, dark: 50 },
  TEXT_STRONGER: { normal: -35, light: -60, dark: 60 },
  STATE_DISABLE: { normal: 36, light: -10, dark: 15 }
}

/**
 * Convert a hex color to HSL (0-360, 0-100, 0-100).
 * @param {string} hex - e.g. #0D6BDE
 * @returns {{ h: number, s: number, l: number } | null}
 */
export function hexToHSL(hex) {
  if (!hex || typeof hex !== 'string') return null
  const raw = hex.replace(/^#/, '')
  if (raw.length !== 6) return null

  const r = parseInt(raw.slice(0, 2), 16) / 255
  const g = parseInt(raw.slice(2, 4), 16) / 255
  const b = parseInt(raw.slice(4, 6), 16) / 255

  const max = Math.max(r, g, b)
  const min = Math.min(r, g, b)
  const delta = max - min
  const l = (max + min) / 2

  let h = 0
  let s = 0
  if (delta !== 0) {
    s = delta / (1 - Math.abs(2 * l - 1))
    if (max === r) h = ((g - b) / delta) % 6
    else if (max === g) h = (b - r) / delta + 2
    else h = (r - g) / delta + 4
    h = h * 60
    if (h < 0) h += 360
  }

  return {
    h: Math.round(h),
    s: Math.round(s * 100),
    l: Math.round(l * 100)
  }
}

/**
 * Convert HSL to hex color.
 * @param {number} h - Hue (0-360)
 * @param {number} s - Saturation (0-100)
 * @param {number} l - Lightness (0-100)
 * @returns {string} Hex color string, e.g. #0D6BDE
 */
export function hslToHex(h, s, l) {
  s = s / 100
  l = l / 100

  const c = (1 - Math.abs(2 * l - 1)) * s
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1))
  const m = l - c / 2

  let r = 0,
    g = 0,
    b = 0
  if (h >= 0 && h < 60) {
    r = c
    g = x
    b = 0
  } else if (h >= 60 && h < 120) {
    r = x
    g = c
    b = 0
  } else if (h >= 120 && h < 180) {
    r = 0
    g = c
    b = x
  } else if (h >= 180 && h < 240) {
    r = 0
    g = x
    b = c
  } else if (h >= 240 && h < 300) {
    r = x
    g = 0
    b = c
  } else {
    r = c
    g = 0
    b = x
  }

  const toHex = (val) => {
    const hex = Math.round((val + m) * 255).toString(16)
    return hex.length === 1 ? '0' + hex : hex
  }

  return `#${toHex(r)}${toHex(g)}${toHex(b)}`.toUpperCase()
}

/**
 * Get brand color variants (hover, press, etc.) from a base color.
 * @param {string} brandColor - Brand color hex string, e.g. #0D6BDE
 * @returns {Object | null} Color variants object with hex values, or null if invalid input
 * @example
 * const variants = getBrandColorVariants('#0D6BDE')
 * // Returns: { primary: '#0D6BDE', hover: '#0C60C8', press: '#084085', ... }
 */
export function getBrandColorVariants(brandColor) {
  if (!brandColor) {
    return
  }
  const hsl = hexToHSL(brandColor)
  if (!hsl) return null

  const { h, s, l } = hsl
  const isLight = l >= LUMINANCE.LIGHT_THRESHOLD
  const isDark = l <= LUMINANCE.DARK_THRESHOLD

  const clampL = (delta) => Math.min(100, Math.max(0, l + delta))

  const pickL = (offset) => {
    if (isLight) return offset.light
    if (isDark) return offset.dark
    return offset.normal
  }

  const computeVariant = (offset) => {
    const deltaL = typeof offset === 'number' ? offset : pickL(offset)
    const lVal = clampL(deltaL)
    return hslToHex(h, s, lVal)
  }

  return {
    primary: brandColor.toUpperCase(),
    hover: computeVariant(L_OFFSET.STATE_HOVER),
    press: computeVariant(L_OFFSET.STATE_PRESS),
    textPrimary: computeVariant(L_OFFSET.TEXT_PRIMARY),
    borderPrimary: computeVariant(L_OFFSET.BORDER_PRIMARY),
    fillSubtler: computeVariant(L_OFFSET.FILL_SUBTLER),
    borderSubtle: computeVariant(L_OFFSET.BORDER_SUBTLE),
    toggleBg: computeVariant(L_OFFSET.TOGGLE_BG),
    textStrong: computeVariant(L_OFFSET.TEXT_STRONG),
    textStronger: computeVariant(L_OFFSET.TEXT_STRONGER),
    stateDisable: computeVariant(L_OFFSET.STATE_DISABLE)
  }
}

/**
 * Inject theme color styles into the page: write --zoom-color-* variables on body.
 * @param {string} [brandColor] - Brand color hex string, e.g. #0D6BDE; skip when empty.
 */
export function applyBrandColor(brandColor) {
  if (!brandColor) return
  const hsl = hexToHSL(brandColor)
  if (!hsl) return

  const cssVars = buildThemeCSSVars(hsl)
  const extraRules = buildBrandExtraRules()
  const styleEl = getOrCreateStyleElement()
  styleEl.textContent = `body {\n${cssVars}\n}\n\n${extraRules}`

  document.body.classList.add('is-brand-color-applied')
}

function getOrCreateStyleElement() {
  let el = document.getElementById(BRAND_STYLE_ID)
  if (!el) {
    el = document.createElement('style')
    el.id = BRAND_STYLE_ID
    el.setAttribute('type', 'text/css')
    document.head.appendChild(el)
  }
  return el
}

/**
 * Build theme CSS variables based on the mapping table in brand-color.md.
 * Formula column order: normal (L>20 & L<80) | ultra-light (L>=80) | ultra-dark (L<=20)
 */
function buildThemeCSSVars({ h, s, l }) {
  const isLight = l >= LUMINANCE.LIGHT_THRESHOLD
  const isDark = l <= LUMINANCE.DARK_THRESHOLD

  const clampL = (delta) => Math.min(100, Math.max(0, l + delta))

  /** Pick L offset by luminance range: normal / light / dark. */
  const pickL = (offset) => {
    if (isLight) return offset.light
    if (isDark) return offset.dark
    return offset.normal
  }

  const hslValue = (offset, alpha) => {
    const deltaL = typeof offset === 'number' ? offset : pickL(offset)
    const lVal = clampL(deltaL)
    const core = `${h}, ${s}%, ${lVal}%`
    return alpha != null ? `hsla(${core}, ${alpha})` : `hsl(${core})`
  }

  const brand = `hsl(${h}, ${s}%, ${l}%)`

  // One-to-one mapping with the variable table in brand-color.md
  const vars = {
    // fill-primary / fill-global-primary: Brand | Brand | Brand
    '--zoom-color-fill-global-primary': brand,
    '--zoom-color-fill-primary': brand,
    // text-primary: Brand | L-40 | L+40
    '--zoom-color-text-primary': hslValue(L_OFFSET.TEXT_PRIMARY),
    // border-primary: L+15 | L-30 | L+30
    '--zoom-color-border-primary': hslValue(L_OFFSET.BORDER_PRIMARY),
    // state-primary-hover: L-10 | L-15 | L+15
    '--zoom-color-state-primary-hover': hslValue(L_OFFSET.STATE_HOVER),
    // state-primary-press: L-25 | L-30 | L+30
    '--zoom-color-state-primary-press': hslValue(L_OFFSET.STATE_PRESS),
    // fill-subtler-primary: L+48 | L(0) | L+70
    '--zoom-color-fill-subtler-primary': hslValue(L_OFFSET.FILL_SUBTLER),
    // state-subtle-primary-hover: Brand 12% | L-20 12% | L+20 12%
    '--zoom-color-state-subtle-primary-hover': hslValue(
      L_OFFSET.STATE_SUBTLE,
      OPACITY.SUBTLE_HOVER
    ),
    // state-subtle-primary-press: Brand 42% | L-20 42% | L+20 42%
    '--zoom-color-state-subtle-primary-press': hslValue(
      L_OFFSET.STATE_SUBTLE,
      OPACITY.SUBTLE_PRESS
    ),
    // border-subtle-primary: L+35 | L-15 | L+50
    '--zoom-color-border-subtle-primary': hslValue(L_OFFSET.BORDER_SUBTLE),
    // toggle-button-bg-selected: L+45 | L-5 | L+60
    '--zoom-color-toggle-button-bg-selected': hslValue(L_OFFSET.TOGGLE_BG),
    // text-strong-primary: L-15 | L-50 | L+50
    '--zoom-color-text-strong-primary': hslValue(L_OFFSET.TEXT_STRONG),
    // text-stronger-primary: L-35 | L-60 | L+60
    '--zoom-color-text-stronger-primary': hslValue(L_OFFSET.TEXT_STRONGER),
    // state-subtle-primary-disable: L+36 | L-10 | L+15 (checkbox disabled color)
    '--zoom-color-state-subtle-primary-disable': hslValue(L_OFFSET.STATE_DISABLE)
  }

  return Object.entries(vars)
    .map(([name, value]) => `  ${name}: ${value};`)
    .join('\n')
}

/**
 * Component-specific CSS rules that apply brand color variables.
 * These rules map UI components to the appropriate CSS variables.
 */
const ACTIVE_COMPONENT_RULES = [
  // Buttons
  {
    selector: '.zm-button--primary',
    styles: { 'background-color': 'var(--zoom-color-fill-global-primary, #0d6bde)' }
  },
  { selector: '.zm-button--text', styles: { color: 'var(--zoom-color-text-primary, #0d6bde)' } },
  {
    selector: '.zm-button--text:hover',
    styles: { color: 'var(--zoom-color-state-primary-hover, #0d6bde)' }
  },

  // Breadcrumb
  {
    selector: '.zm-breadcrumb__inner a, .zm-breadcrumb__inner.is-link',
    styles: { color: 'var(--zoom-color-text-primary, #0d6bde)' }
  },

  // Tabs
  {
    selector: '.zm-tabs__item.is-active',
    styles: { color: 'var(--zoom-color-text-primary, #0d6bde)' }
  },
  {
    selector: '.zm-tabs__item.is-active:after',
    styles: { 'background-color': 'var(--zoom-color-fill-global-primary, #0d6bde)' }
  },
  {
    selector: '.zm-tabs__item.is-active h2',
    styles: { color: 'var(--zoom-color-text-primary, #0d6bde) !important' }
  },

  // Tab Navigation
  {
    selector: '.zm-tabnav>li>a:hover',
    styles: { color: 'var(--zoom-color-state-primary-hover, #0d6bde)' }
  },
  {
    selector: '.zm-tabnav>li.active>a, .zm-tabnav>li.active>a:hover, .zm-tabnav>li.active>a:focus',
    styles: {
      color: 'var(--zoom-color-text-primary, #0d6bde)',
      'border-bottom-color': 'var(--zoom-color-fill-global-primary, #0d6bde)'
    }
  },

  // Checkbox
  {
    selector: '.zm-checkbox__label.is-checked .zm-checkbox__inner',
    styles: {
      'background-color': 'var(--zoom-color-fill-global-primary, #0d6bde)',
      'border-color': 'var(--zoom-color-fill-global-primary, #0d6bde)'
    }
  },
  {
    selector: '.zm-checkbox__label.is-checked:hover .zm-checkbox__inner',
    styles: {
      'background-color': 'var(--zoom-color-state-primary-hover, #0d6bde)',
      'border-color': 'var(--zoom-color-state-primary-hover, #0d6bde)'
    }
  },

  // Switch
  {
    selector: '.zm-switch.is-checked .zm-switch__core',
    styles: { 'background-color': 'var(--zoom-color-fill-global-primary, #0d6bde)' }
  },
  {
    selector: '.zm-switch.is-checked .zm-switch__core:hover',
    styles: { 'background-color': 'var(--zoom-color-state-primary-hover, #0d6bde)' }
  }
]

/**
 * Disabled component rules (temporarily disabled, kept for reference).
 * To enable: move items to ACTIVE_COMPONENT_RULES.
 * @type {Array<{selector: string, styles: Object}>}
 */
// eslint-disable-next-line no-unused-vars
const DISABLED_COMPONENT_RULES = [
  // Old .zoom-* prefix components (replaced by .zm-* components)
  { selector: '.zoom-link', styles: { color: 'var(--zoom-color-text-primary, #0d6bde)' } },
  {
    selector: '.zoom-link:hover',
    styles: { color: 'var(--zoom-color-state-primary-hover, #0d6bde)' }
  },
  {
    selector: '.zoom-button--primary',
    styles: { 'background-color': 'var(--zoom-color-fill-global-primary, #0d6bde)' }
  },
  {
    selector: '.zoom-button--primary:hover:not(.is-loading)',
    styles: { 'background-color': 'var(--zoom-color-state-primary-hover, #0d6bde)' }
  },
  {
    selector: '.zoom-breadcrumb__inner.is-link',
    styles: { color: 'var(--zoom-color-text-primary, #0d6bde)' }
  },
  {
    selector: '.zoom-button--tertiary',
    styles: { color: 'var(--zoom-color-text-primary, #0d6bde)' }
  },
  {
    selector: '.zoom-button--tertiary:hover:not(.is-loading)',
    styles: { color: 'var(--zoom-color-state-primary-hover, #0d6bde)' }
  },
  { selector: '.zoom-button--text', styles: { color: 'var(--zoom-color-text-primary, #0d6bde)' } },
  {
    selector: '.zoom-button--text:hover:not(.is-loading)',
    styles: { color: 'var(--zoom-color-state-primary-hover, #0d6bde)' }
  },
  {
    selector: '.zoom-tabs__item.is-active',
    styles: { color: 'var(--zoom-color-text-primary, #0d6bde)' }
  },
  {
    selector: '.zoom-tabs__active-bar',
    styles: { 'background-color': 'var(--zoom-color-fill-global-primary, #0d6bde)' }
  },
  {
    selector: '.zoom-checkbox__wrap.is-checked .zoom-checkbox__inner',
    styles: {
      'background-color': 'var(--zoom-color-fill-global-primary, #0d6bde)',
      'border-color': 'var(--zoom-color-fill-global-primary, #0d6bde)'
    }
  },
  {
    selector: '.zoom-checkbox__wrap.is-checked:not(.is-disabled):hover .zoom-checkbox__inner',
    styles: {
      'background-color': 'var(--zoom-color-state-primary-hover, #0d6bde)',
      'border-color': 'var(--zoom-color-state-primary-hover, #0d6bde)'
    }
  },
  {
    selector: '.zoom-checkbox__wrap.is-indeterminate .zoom-checkbox__inner',
    styles: {
      'background-color': 'var(--zoom-color-fill-global-primary, #0d6bde)',
      'border-color': 'var(--zoom-color-fill-global-primary, #0d6bde)'
    }
  },
  {
    selector: '.zoom-checkbox__wrap.is-indeterminate:not(.is-disabled):hover .zoom-checkbox__inner',
    styles: {
      'background-color': 'var(--zoom-color-state-primary-hover, #0d6bde)',
      'border-color': 'var(--zoom-color-state-primary-hover, #0d6bde)'
    }
  },
  {
    selector: '.zoom-toggle.is-checked .zoom-toggle__core',
    styles: { 'background-color': 'var(--zoom-color-fill-global-primary, #0d6bde)' }
  },
  {
    selector: '.zoom-toggle.is-checked .zoom-toggle__core:hover',
    styles: { 'background-color': 'var(--zoom-color-state-primary-hover, #0d6bde)' }
  },
  {
    selector: '.zoom-pagination-pager li.is-active',
    styles: { 'background-color': 'var(--zoom-color-fill-global-primary, #0d6bde)' }
  },
  {
    selector: '.zoom-pagination-pager li',
    styles: { color: 'var(--zoom-color-text-primary, #0d6bde)' }
  },
  {
    selector: '.zm-pager li.active',
    styles: { 'background-color': 'var(--zoom-color-fill-global-primary, #0d6bde)' }
  },
  {
    selector: '.zoom-step__title.is-process',
    styles: { color: 'var(--zoom-color-text-primary, #0d6bde)' }
  },
  {
    selector: '.zoom-step__faker-icon',
    styles: { 'border-color': 'var(--zoom-color-fill-global-primary, #0d6bde)' }
  },
  {
    selector: '.zoom-segment-item.is-active',
    styles: { color: 'var(--zoom-color-text-primary, #0d6bde)' }
  },
  {
    selector: '.zm-loading-spinner .zm-loading-text',
    styles: { color: 'var(--zoom-color-text-primary, #0d6bde)' }
  }
]

/**
 * Generate CSS rules from component style configuration.
 */
function buildBrandExtraRules() {
  return ACTIVE_COMPONENT_RULES.map(({ selector, styles }) => {
    const styleStr = Object.entries(styles)
      .map(([prop, value]) => `  ${prop}: ${value};`)
      .join('\n')
    return `${selector} {\n${styleStr}\n}`
  }).join('\n\n')
}
